import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Plus,
  User,
  EnvelopeSimple,
  Buildings,
  DotsThreeVertical,
  PencilSimple,
  Trash,
  Key,
} from "@phosphor-icons/react";

export default function ClientUsers() {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", company_id: "", role: "client" });

  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", company_id: "", role: "client" });

  const [resetting, setResetting] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  const load = async () => {
    const [u, c] = await Promise.all([api.get("/auth/client-users"), api.get("/companies")]);
    setUsers(u.data);
    setCompanies(c.data);
  };
  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/create-client-user", form);
      toast.success(`Client user created — share login: ${form.email}`);
      setOpen(false);
      setForm({ name: "", email: "", password: "", company_id: "", role: "client" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create");
    }
  };

  const openEdit = (u) => {
    setEditing(u);
    setEditForm({ name: u.name, email: u.email, company_id: u.company_id || "", role: u.role || "client" });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/auth/client-users/${editing.id}`, editForm);
      toast.success("Client user updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const openResetPwd = (u) => {
    setResetting(u);
    setNewPassword("");
  };

  const saveResetPwd = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      await api.put(`/auth/client-users/${resetting.id}`, { password: newPassword });
      toast.success(`Password reset for ${resetting.name}. Share the new password: ${newPassword}`);
      setResetting(null);
      setNewPassword("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete client user "${u.name}" (${u.email})? They will no longer be able to log in.`)) return;
    try {
      await api.delete(`/auth/client-users/${u.id}`);
      toast.success(`Deleted ${u.name}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const [partnerOpen, setPartnerOpen] = useState(false);
  const [partnerForm, setPartnerForm] = useState({ name: "", email: "", password: "", role: "audit_team" });

  const createAuditor = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/register-audit", { ...partnerForm });
      const niceRole = partnerForm.role === "audit_partner" ? "Audit Partner" : "Audit Team Member";
      toast.success(`${niceRole} created — share login: ${partnerForm.email}`);
      setPartnerOpen(false);
      setPartnerForm({ name: "", email: "", password: "", role: "audit_team" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create auditor");
    }
  };

  const header = (
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Geist" }}>Client Users</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Create logins for client contacts and Founders. Use <span className="font-medium">Invite Auditor</span> to add a Team Member or a read-only Partner.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Dialog open={partnerOpen} onOpenChange={setPartnerOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="add-audit-partner-btn">
              <Plus size={14} className="mr-1" /> Invite Auditor
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="new-audit-partner-dialog">
            <DialogHeader><DialogTitle>Invite an audit user</DialogTitle></DialogHeader>
            <form onSubmit={createAuditor} className="space-y-3">
              <div>
                <Label>Role</Label>
                <Select value={partnerForm.role} onValueChange={(v) => setPartnerForm({ ...partnerForm, role: v })}>
                  <SelectTrigger data-testid="auditor-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="audit_team">Audit Team Member — full access (write)</SelectItem>
                    <SelectItem value="audit_partner">Audit Partner — read-only firm-wide view</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Full Name</Label>
                <Input value={partnerForm.name} onChange={(e) => setPartnerForm({ ...partnerForm, name: e.target.value })} required data-testid="partner-name-input" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={partnerForm.email} onChange={(e) => setPartnerForm({ ...partnerForm, email: e.target.value })} required data-testid="partner-email-input" />
              </div>
              <div>
                <Label>Initial Password</Label>
                <Input value={partnerForm.password} onChange={(e) => setPartnerForm({ ...partnerForm, password: e.target.value })} required minLength={6} data-testid="partner-password-input" />
              </div>
              <p className="text-[11px] text-slate-500">
                Team Members can create/edit observations and manage users. Partners get a firm-wide
                trend dashboard with drill-in, but cannot edit anything.
              </p>
              <DialogFooter>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="partner-save-btn">Create Auditor</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-client-user-btn">
            <Plus size={14} className="mr-1" /> New Client User
          </Button>
        </DialogTrigger>
        <DialogContent data-testid="new-client-user-dialog">
          <DialogHeader><DialogTitle>Create client user</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div>
              <Label>Full Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="cu-name-input" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="cu-email-input" />
            </div>
            <div>
              <Label>Initial Password</Label>
              <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} data-testid="cu-password-input" />
            </div>
            <div>
              <Label>Company</Label>
              <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger data-testid="cu-company-select"><SelectValue placeholder="Choose company" /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="cu-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Client User — operational (respond, attach evidence)</SelectItem>
                  <SelectItem value="client_founder">Founder — executive trend view (read-only)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" disabled={!form.company_id} data-testid="cu-save-btn">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );

  return (
    <AppLayout header={header}>
      {users.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-md p-12 text-center" data-testid="empty-client-users">
          <User size={32} className="mx-auto text-slate-400" />
          <h3 className="text-base font-semibold mt-3">No client users yet</h3>
          <p className="text-sm text-slate-500 mt-1">Create the first client login to give them access.</p>
        </div>
      ) : (
        <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4 py-2 text-left">Email</th>
                <th className="px-4 py-2 text-left">Role</th>
                <th className="px-4 py-2 text-left">Company</th>
                <th className="px-4 py-2 text-right w-12">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const c = companies.find((x) => x.id === u.company_id);
                return (
                  <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`client-user-row-${u.id}`}>
                    <td className="px-4 py-3 font-medium">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-semibold">
                          {u.name?.[0]?.toUpperCase() || "?"}
                        </div>
                        {u.name}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      <div className="flex items-center gap-2">
                        <EnvelopeSimple size={14} className="text-slate-400" />
                        <a href={`mailto:${u.email}`} className="hover:underline">{u.email}</a>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {u.role === "client_founder" ? (
                        <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border bg-[#0B2A5B] text-white border-[#0B2A5B]">
                          Founder
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border bg-slate-100 text-slate-700 border-slate-200">
                          Client
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      <div className="flex items-center gap-2">
                        <Buildings size={14} className="text-slate-400" />
                        {c?.name || "—"}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="p-1.5 hover:bg-slate-100 rounded text-slate-500"
                            data-testid={`user-menu-${u.id}`}
                          >
                            <DotsThreeVertical size={16} weight="bold" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(u)} data-testid={`user-edit-${u.id}`}>
                            <PencilSimple size={13} className="mr-2" /> Edit details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openResetPwd(u)} data-testid={`user-reset-pwd-${u.id}`}>
                            <Key size={13} className="mr-2" /> Reset password
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => deleteUser(u)}
                            className="text-red-600 focus:text-red-700"
                            data-testid={`user-delete-${u.id}`}
                          >
                            <Trash size={13} className="mr-2" /> Delete user
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Edit client user</DialogTitle>
          </DialogHeader>
          {editing && (
            <form onSubmit={saveEdit} className="space-y-3">
              <div>
                <Label>Full Name</Label>
                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required data-testid="edit-user-name" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} required data-testid="edit-user-email" />
              </div>
              <div>
                <Label>Company</Label>
                <Select value={editForm.company_id} onValueChange={(v) => setEditForm({ ...editForm, company_id: v })}>
                  <SelectTrigger data-testid="edit-user-company"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Role</Label>
                <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                  <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client">Client User</SelectItem>
                    <SelectItem value="client_founder">Founder (executive view)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="edit-user-save">Save changes</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetting} onOpenChange={(v) => !v && setResetting(null)}>
        <DialogContent data-testid="reset-pwd-dialog">
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
          </DialogHeader>
          {resetting && (
            <form onSubmit={saveResetPwd} className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
                You&apos;re setting a new password for <b>{resetting.name}</b> ({resetting.email}). Share the new password securely after saving.
              </div>
              <div>
                <Label>New Password</Label>
                <Input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder="At least 6 characters"
                  data-testid="reset-pwd-input"
                />
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="reset-pwd-save">Reset & Save</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
